import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Ticket } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Head } from "@/ui/frame/Page";
import { Button } from "@/ui/Button";
import { Filter } from "@/ui/Filter";
import { Chip } from "@/ui/Chip";
import { Lamp } from "@/ui/Lamp";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { absoluteTime, relativeTime } from "@/lib/format";
import {
  CATEGORY_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  isOpen,
  serviceSummary,
} from "@/features/support/tickets";
import { NewTicketDialog } from "@/features/support/NewTicketDialog";

export const Route = createFileRoute("/_app/o/$orgId/support/")({
  component: SupportScreen,
  // The filter is in the address, like every other filtered list here, so a
  // screen somebody is looking at can be pasted into a message.
  validateSearch: (search: Record<string, unknown>): { show?: "open" } =>
    search.show === "open" ? { show: "open" } : {},
});

/**
 * The customer's support tickets.
 *
 * # WHY THE PRODUCT HAS THIS AT ALL
 *
 * Because some things a customer must be able to ask for cannot be a form.
 * Changing the legal name on the account is the one that forced it: the name
 * is already printed on every invoice issued, so it is not a field somebody
 * edits — it is a request a person has to look at. Before this, the only
 * channel was an address in a page footer: no state, no history the customer
 * can read back, and no way of telling whether anybody saw it.
 */
function SupportScreen() {
  const { orgId } = Route.useParams();
  const { show } = Route.useSearch();
  const navigate = useNavigate();
  const [composing, setComposing] = useState(false);
  const onlyOpen = show === "open";

  const tickets = useQuery({
    queryKey: ["tickets", orgId, onlyOpen],
    queryFn: ({ signal }) =>
      api.get<{ tickets: Ticket[] | null }>(
        `/organisations/${orgId}/tickets`,
        onlyOpen ? { open: true } : undefined,
        signal,
      ),
  });

  const list = tickets.data?.tickets ?? [];
  const openCount = list.filter((t) => isOpen(t.status)).length;

  return (
    <div className="flex flex-col gap-6">
      <Head
        title="Support"
        description="Ask us something. Every member of this organisation can see the answer."
        actions={
          <Button tone="primary" onClick={() => setComposing(true)}>
            New request
          </Button>
        }
      />

      <NewTicketDialog
        open={composing}
        orgId={orgId}
        onClose={() => setComposing(false)}
        onOpened={(id) => {
          setComposing(false);
          void navigate({
            to: "/o/$orgId/support/$ticketId",
            params: { orgId, ticketId: id },
          });
        }}
      />

      <Panel
        padded={false}
        toolbar={
          <>
            <Filter
              label="All"
              count={list.length}
              active={!onlyOpen}
              onClick={() => void navigate({ to: ".", search: {} })}
            />
            <Filter
              label="Not finished"
              count={onlyOpen ? list.length : openCount}
              active={onlyOpen}
              onClick={() => void navigate({ to: ".", search: { show: "open" } })}
            />
          </>
        }
      >
        {tickets.isLoading ? (
          <TableSkeleton rows={4} columns={3} />
        ) : tickets.isError ? (
          <div className="p-[18px]">
            <ErrorState error={tickets.error} onRetry={() => void tickets.refetch()} />
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            title={onlyOpen ? "Nothing open" : "No requests yet"}
            description={
              onlyOpen
                ? "Everything you have asked us has been answered."
                : "When you need something changed that you cannot change yourself, ask here."
            }
            action={
              !onlyOpen ? (
                <Button tone="primary" onClick={() => setComposing(true)}>
                  New request
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="divide-y divide-[var(--border)]">
            {list.map((t) => (
              <li key={t.id}>
                <Link
                  to="/o/$orgId/support/$ticketId"
                  params={{ orgId, ticketId: t.id }}
                  className="flex items-center gap-3 px-[18px] py-3 transition-colors hover:bg-[var(--surface-2)]"
                >
                  <TicketStatusPill status={t.status} />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="t-strong truncate">{t.subject}</span>
                    <span className="t-meta truncate">
                      {CATEGORY_LABEL[t.category]} · opened by {t.opened_by_name}
                      {/* The one service figure worth showing a customer: not
                          how long it has been open, but whether a person has
                          answered at all. */}
                      {!t.first_reply_at && isOpen(t.status) ? " · not yet answered" : ""}
                      {/* Which services, on the row. Somebody who has three
                          open technical requests should be able to tell them
                          apart without opening each one. */}
                      {serviceSummary(t.services) ? ` · ${serviceSummary(t.services)}` : ""}
                    </span>
                  </span>
                  <Chip className="hidden shrink-0 sm:inline-flex">{t.reference}</Chip>
                  <Chip>
                    {t.message_count} {t.message_count === 1 ? "message" : "messages"}
                  </Chip>
                  <span
                    className="t-meta hidden shrink-0 tabular-nums lg:block"
                    title={absoluteTime(t.last_message_at)}
                  >
                    {relativeTime(t.last_message_at)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

/** The status, in the customer's words and the shared state colours. */
/** A ticket's state, in the customer's words. The system's chip. */
export function TicketStatusPill({ status }: { status: Ticket["status"] }) {
  const token = STATUS_TONE[status];
  return (
    <span className="chip gap-1.5" style={{ color: LAMP_FOR[token], borderColor: LAMP_FOR[token] }}>
      <Lamp live={status === "waiting_on_us" || status === "open"} />
      {STATUS_LABEL[status]}
    </span>
  );
}

/**
 * The lamp a state name lights.
 *
 * The screens used to compose a custom property name out of a variable —
 * `var(--color-state-${token}-fg)` — which works until the token set is
 * replaced and then fails silently, because a missing custom property is not
 * an error, it is simply nothing. This is a lookup, so an unknown state is a
 * compile-time concern rather than an invisible one.
 */
const LAMP_FOR: Record<string, string> = {
  running: "var(--success)",
  healthy: "var(--success)",
  degraded: "var(--warning)",
  suspended: "var(--warning)",
  failed: "var(--danger)",
  stopped: "var(--text-faint)",
  building: "var(--primary)",
};
